import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  HStack,
  Box,
  Image,
  useToast,
  Spinner,
} from '@chakra-ui/react';

const SUBMIT_CONTACT_FORM = gql`
  mutation SubmitContactForm($input: SubmitContactFormInput!) {
    submitContactForm(input: $input) {
      id
      firstName
      lastName
      email
      telegram
      sessionId
      createdAt
    }
  }
`;

interface ListBuildingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ListBuildingModal: React.FC<ListBuildingModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    telegram: '',
    sessionId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const toast = useToast();

  const [submitContactForm, { loading }] = useMutation(SUBMIT_CONTACT_FORM, {
    context: {
      headers: {
        'x-tenant-id': '691d16f575ae51bfe1adf3e8' // Block52 tenant ID
      }
    },
    onCompleted: () => {
      toast({
        title: 'Success!',
        description: "Thanks for your interest! We'll be in touch soon.",
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    await submitContactForm({
      variables: {
        input: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          telegram: formData.telegram || undefined,
          sessionId: formData.sessionId || undefined,
          formType: 'contact',
          submissionUrl: window.location.href,
          notificationEmails: [
            // 'dmd.dzian@gmail.com',
            'tom@tommillerservices.com',
            // 'lucas@lucascullen.com',
            // 'max@supermassive.group',
            // 'steve@supermassive.group'
          ]
        }
      }
    });
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent
        bg="gray.800"
        borderRadius="xl"
        border="1px solid"
        borderColor="blue.400"
        position="relative"
        overflow="hidden"
      >
        {/* Web3 styled background */}
        <Box
          position="absolute"
          inset="0"
          bgGradient="linear(to-br, blue.600, purple.600)"
          opacity="0.1"
          borderRadius="xl"
        />
        <Box
          position="absolute"
          top="0"
          left="0"
          w="full"
          h="1"
          bgGradient="linear(to-r, blue.500, purple.500, blue.500)"
          animation="pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
        />

        <ModalCloseButton color="white" zIndex="2" />

        <ModalBody pt={8} pb={8} position="relative" zIndex="1">
          <VStack spacing={6}>
            {/* Logo */}
            <Image src="/block52-logo-color.png" alt="Block52 Logo" h="64px" w="auto" objectFit="contain" />

            {/* Icon */}
            <Box
              w="64px"
              h="64px"
              borderRadius="full"
              bgGradient="linear(to-br, blue.500, purple.500)"
              opacity="0.2"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="1px solid"
              borderColor="blue.400"
              position="relative"
            >
              <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#60A5FA' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </Box>

            {/* Header */}
            <VStack spacing={2}>
              <Text fontSize="2xl" fontWeight="bold" color="white" textAlign="center">
                Start Your Card Game Club
              </Text>
              <Text fontSize="sm" color="gray.300" textAlign="center">
                Get started with Block52 today. Enter your details below and we'll reach out to help you launch your club.
              </Text>
            </VStack>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <VStack spacing={4} w="full">
                {/* First Name */}
                <FormControl isInvalid={!!errors.firstName}>
                  <FormLabel color="gray.300" fontSize="sm">First Name *</FormLabel>
                  <Input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    placeholder="John"
                    bg="gray.700"
                    border="1px solid"
                    borderColor={errors.firstName ? 'red.500' : 'blue.500'}
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                  />
                  {errors.firstName && (
                    <Text color="red.400" fontSize="xs" mt={1}>{errors.firstName}</Text>
                  )}
                </FormControl>

                {/* Last Name */}
                <FormControl isInvalid={!!errors.lastName}>
                  <FormLabel color="gray.300" fontSize="sm">Last Name *</FormLabel>
                  <Input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    placeholder="Doe"
                    bg="gray.700"
                    border="1px solid"
                    borderColor={errors.lastName ? 'red.500' : 'blue.500'}
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                  />
                  {errors.lastName && (
                    <Text color="red.400" fontSize="xs" mt={1}>{errors.lastName}</Text>
                  )}
                </FormControl>

                {/* Email */}
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel color="gray.300" fontSize="sm">Email *</FormLabel>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="john@example.com"
                    bg="gray.700"
                    border="1px solid"
                    borderColor={errors.email ? 'red.500' : 'blue.500'}
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                  />
                  {errors.email && (
                    <Text color="red.400" fontSize="xs" mt={1}>{errors.email}</Text>
                  )}
                </FormControl>

                {/* Telegram (Optional) */}
                <FormControl>
                  <FormLabel color="gray.300" fontSize="sm">Telegram (Optional)</FormLabel>
                  <Input
                    type="text"
                    value={formData.telegram}
                    onChange={(e) => handleChange('telegram', e.target.value)}
                    placeholder="@username"
                    bg="gray.700"
                    border="1px solid"
                    borderColor="blue.500"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                  />
                </FormControl>

                {/* Session ID (Optional) */}
                <FormControl>
                  <FormLabel color="gray.300" fontSize="sm">Session ID (Optional)</FormLabel>
                  <Input
                    type="text"
                    value={formData.sessionId}
                    onChange={(e) => handleChange('sessionId', e.target.value)}
                    placeholder="Your Session ID"
                    bg="gray.700"
                    border="1px solid"
                    borderColor="blue.500"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                  />
                </FormControl>

                {/* Buttons */}
                <HStack spacing={3} w="full" mt={2}>
                  <Button
                    flex="1"
                    onClick={onClose}
                    isDisabled={loading}
                    bg="gray.600"
                    color="white"
                    _hover={{ bg: 'gray.700' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    flex="1"
                    type="submit"
                    isLoading={loading}
                    loadingText="Submitting..."
                    spinner={
                      <svg
                        className="animate-spin"
                        style={{ height: '20px', width: '20px' }}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    }
                    bgGradient="linear(to-r, blue.500, purple.500)"
                    color="white"
                    _hover={{
                      bgGradient: 'linear(to-r, blue.600, purple.600)',
                      transform: 'scale(1.05)',
                    }}
                    transition="all 0.3s"
                  >
                    Get Started
                  </Button>
                </HStack>
              </VStack>
            </form>

            {/* Footer */}
            <VStack spacing={2} pt={4}>
              <Text fontSize="xs" color="gray.400" textAlign="center">
                We'll contact you within 24 hours to discuss your card game club
              </Text>
              <HStack spacing={1}>
                <Box w="2" h="2" borderRadius="full" bg="blue.400" animation="pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite" />
                <Text fontSize="xs" color="gray.400">Powered by Block52</Text>
              </HStack>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ListBuildingModal;
